import { ACTION_TYPES } from '../../models/aiAnalysis/workflow'
import type { Action, DraftAction, ForwardAckActionType } from '../../models/aiAnalysis/workflow'

// The popover and the suggested section both route through these helpers, so
// one payload renders the same decide UI on either surface.

export interface DraftActionSet {
  forwardAction?: DraftAction | null
  acknowledgeAction?: DraftAction | null
  combinedToClientAction?: DraftAction | null
  combinedToNotaryAction?: DraftAction | null
}

// At most one anchored row serializes this payload — latest_pending_ai_draft is singular.
export const readAnchoredActionSet = (): DraftActionSet | null => {
  const raw = document.querySelector('[data-ai-draft-payload]')?.getAttribute('data-react-props')
  if (!raw) return null

  try {
    const { forwardAction, acknowledgeAction, combinedToClientAction, combinedToNotaryAction } = JSON.parse(raw)
    return { forwardAction, acknowledgeAction, combinedToClientAction, combinedToNotaryAction }
  } catch {
    return null
  }
}

// Card order: forward before acknowledge for legacy pairs; combined_to_client
// before combined_to_notary.
export const buildActions = ({
  forwardAction,
  acknowledgeAction,
  combinedToClientAction,
  combinedToNotaryAction
}: DraftActionSet): DraftAction[] => {
  return [forwardAction, acknowledgeAction, combinedToClientAction, combinedToNotaryAction].filter(
    (action): action is DraftAction => action != null
  )
}

export const isCombinedAction = (action: Action): boolean =>
  action.action_type === ACTION_TYPES.COMBINED_TO_CLIENT || action.action_type === ACTION_TYPES.COMBINED_TO_NOTARY

// The combine engine supersedes a legacy forward/acknowledge pair whenever it
// creates a combined action — combined and legacy actions never co-exist in
// one payload. This flag alone is enough to route between the two UIs.
export const hasCombinedActions = (actions: DraftAction[]): boolean => actions.some(isCombinedAction)

const preDecidedPlaceholder = (actionType: ForwardAckActionType): DraftAction => ({
  id: `pre-decided-${actionType}`,
  action_type: actionType,
  action_payload: { text: '' },
  order_id: 0,
  created_at: '',
  updated_at: '',
  decided_at: new Date().toISOString(),
  decision: 'approved'
})

// The backend only returns pending actions; when one half of the pair was
// decided externally, a pre-decided placeholder stands in and the
// orchestrator's decided handling skips that step.
export const resolveLegacyPair = ({
  forwardAction,
  acknowledgeAction
}: Pick<DraftActionSet, 'forwardAction' | 'acknowledgeAction'>): {
  forwardAction: DraftAction
  acknowledgeAction: DraftAction
} => ({
  forwardAction: forwardAction ?? preDecidedPlaceholder(ACTION_TYPES.FORWARD),
  acknowledgeAction: acknowledgeAction ?? preDecidedPlaceholder(ACTION_TYPES.ACKNOWLEDGE)
})
